/* ==========================================================================
   Intake Form Page
   Task F34: Post-payment intake form for new pharmacy setup
   Route: /intake/:token (public, no auth)
   ========================================================================== */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { AlertCircle } from 'lucide-react'
import { MarketingLayout } from '@/components/layout/MarketingLayout'
import { Card } from '@/components/ui/Card'
import { FormField } from '@/components/ui/FormField'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Button } from '@/components/ui/Button'
import { FullPageSpinner } from '@/components/ui/Spinner'
import { FileUploadField } from '@/components/intake/FileUploadField'
import { ColorPickerField } from '@/components/intake/ColorPickerField'
import { SlugInput } from '@/components/intake/SlugInput'
import { CalendarEmbed } from '@/components/intake/CalendarEmbed'
import { IntakeConfirmation } from '@/components/intake/IntakeConfirmation'
import {
  getIntakeFormData,
  submitIntakeForm,
  type IntakeFormData,
  type IntakeSubmitRequest,
} from '@/api/intake'

/* --------------------------------------------------------------------------
   Phone formatting helpers
   -------------------------------------------------------------------------- */

function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '')
  const trimmed = digits.slice(0, 10)

  if (trimmed.length === 0) return ''
  if (trimmed.length <= 3) return `(${trimmed}`
  if (trimmed.length <= 6) return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`
  return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`
}

function stripPhoneFormatting(value: string): string {
  return value.replace(/\D/g, '')
}

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface IntakeFormValues {
  pharmacy_name: string
  admin_email: string
  fax_number: string
  logo_file_url: string | null
  logo_file_name: string | null
  primary_color: string
  secondary_color: string
  formulary_file_url: string | null
  formulary_file_name: string | null
  preferred_slug: string
  notes: string
}

type PageState = 'loading' | 'form' | 'submitted' | 'already_submitted' | 'error'

/* --------------------------------------------------------------------------
   Nav (consistent with other marketing pages)
   -------------------------------------------------------------------------- */

const nav = (
  <>
    <a
      href="/#pricing"
      className="text-sm text-charcoal hover:text-deep-purple transition-colors"
    >
      Pricing
    </a>
    <Link
      to="/admin/login"
      className="text-sm text-deep-purple hover:underline font-medium"
    >
      Login
    </Link>
  </>
)

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function IntakeForm() {
  const { token } = useParams<{ token: string }>()
  const [pageState, setPageState] = useState<PageState>('loading')
  const [formData, setFormData] = useState<IntakeFormData | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [submitError, setSubmitError] = useState<string>('')

  const methods = useForm<IntakeFormValues>({
    defaultValues: {
      pharmacy_name: '',
      admin_email: '',
      fax_number: '',
      logo_file_url: null,
      logo_file_name: null,
      primary_color: '',
      secondary_color: '',
      formulary_file_url: null,
      formulary_file_name: null,
      preferred_slug: '',
      notes: '',
    },
    mode: 'onBlur',
  })

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = methods

  // Watch values for controlled components
  const logoFileUrl = watch('logo_file_url')
  const logoFileName = watch('logo_file_name')
  const formularyFileUrl = watch('formulary_file_url')
  const formularyFileName = watch('formulary_file_name')
  const primaryColor = watch('primary_color')
  const secondaryColor = watch('secondary_color')
  const preferredSlug = watch('preferred_slug')

  // Load form data on mount
  useEffect(() => {
    if (!token) {
      setErrorMessage('No intake token provided.')
      setPageState('error')
      return
    }

    async function loadData() {
      try {
        const data = await getIntakeFormData(token!)
        setFormData(data)

        // Pre-fill admin email from subscription contact
        setValue('admin_email', data.contact_email)

        if (data.already_submitted) {
          setPageState('already_submitted')
        } else {
          setPageState('form')
        }
      } catch (err: unknown) {
        if (typeof err === 'object' && err !== null && 'response' in err) {
          const axiosErr = err as { response?: { status?: number } }
          if (axiosErr.response?.status === 404) {
            setErrorMessage(
              'This intake link is invalid or has expired. Please check your email for a valid link, or contact support@tjmlabs.com.',
            )
          } else {
            setErrorMessage('Something went wrong. Please try again later.')
          }
        } else {
          setErrorMessage('Unable to load the intake form. Please check your connection.')
        }
        setPageState('error')
      }
    }

    loadData()
  }, [token, setValue])

  // Handle form submission
  const onSubmit = async (values: IntakeFormValues) => {
    if (!token) return
    setSubmitError('')

    const payload: IntakeSubmitRequest = {
      pharmacy_name: values.pharmacy_name,
      admin_email: values.admin_email,
      fax_number: stripPhoneFormatting(values.fax_number),
      preferred_slug: values.preferred_slug,
      logo_file_url: values.logo_file_url || null,
      primary_color: values.primary_color || null,
      secondary_color: values.secondary_color || null,
      formulary_file_url: values.formulary_file_url || null,
      formulary_file_name: values.formulary_file_name || null,
      notes: values.notes || null,
    }

    try {
      await submitIntakeForm(token, payload)
      setPageState('submitted')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosErr = err as { response?: { status?: number; data?: { detail?: string } } }
        const detail = axiosErr.response?.data?.detail
        if (axiosErr.response?.status === 409) {
          setSubmitError(detail || 'A submission already exists or the slug is taken.')
        } else if (axiosErr.response?.status === 422) {
          setSubmitError(detail || 'Please check your input and try again.')
        } else {
          setSubmitError(detail || 'Something went wrong. Please try again.')
        }
      } else {
        setSubmitError('Unable to submit. Please check your connection and try again.')
      }
    }
  }

  // Render based on page state
  const renderContent = () => {
    switch (pageState) {
      case 'loading':
        return <FullPageSpinner text="Loading intake form..." />

      case 'error':
        return (
          <div className="max-w-lg mx-auto text-center py-16">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mb-6">
              <AlertCircle className="h-7 w-7 text-error" aria-hidden="true" />
            </div>
            <h1 className="font-serif text-2xl text-charcoal mb-3">
              Unable to load intake form
            </h1>
            <p className="text-charcoal/70 mb-6">{errorMessage}</p>
            <a
              href="mailto:support@tjmlabs.com"
              className="text-deep-purple hover:underline font-medium"
            >
              Contact support
            </a>
          </div>
        )

      case 'already_submitted':
        return (
          <div className="max-w-lg mx-auto text-center py-16">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 mb-6">
              <AlertCircle className="h-7 w-7 text-blue-600" aria-hidden="true" />
            </div>
            <h1 className="font-serif text-2xl text-charcoal mb-3">
              Intake form already submitted
            </h1>
            <p className="text-charcoal/70 mb-6">
              An intake form has already been submitted for this subscription.
              Our team is working on setting up your portal.
            </p>
            <p className="text-sm text-charcoal/60">
              Questions?{' '}
              <a
                href="mailto:onboarding@tjmlabs.com"
                className="text-deep-purple hover:underline"
              >
                onboarding@tjmlabs.com
              </a>
            </p>
          </div>
        )

      case 'submitted':
        return (
          <div className="py-16 px-4 sm:px-6 lg:px-8">
            <IntakeConfirmation />
          </div>
        )

      case 'form':
        return (
          <div className="py-12 px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="max-w-2xl mx-auto text-center mb-10">
              <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mb-3">
                Set up your pharmacy portal
              </h1>
              <p className="text-lg text-charcoal/70">
                Tell us about your pharmacy so we can configure your branded provider portal.
              </p>
              {formData && (
                <p className="mt-2 text-sm text-charcoal/50">
                  {formData.tier.charAt(0).toUpperCase() + formData.tier.slice(1)} plan
                  {' '}&middot;{' '}
                  {formData.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} billing
                </p>
              )}
            </div>

            {/* Form */}
            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="max-w-6xl mx-auto space-y-8"
                noValidate
              >
                {/* Submit error */}
                {submitError && (
                  <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-error flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{submitError}</p>
                  </div>
                )}

                {/* Section 1: Pharmacy Info */}
                <Card header="Pharmacy Information" className="max-w-2xl mx-auto">
                  <div className="space-y-4">
                    <FormField
                      label="Pharmacy Name"
                      required
                      error={errors.pharmacy_name?.message}
                    >
                      <Input
                        {...register('pharmacy_name', {
                          required: 'Pharmacy name is required',
                          maxLength: { value: 255, message: 'Too long' },
                        })}
                        placeholder="e.g., Precision Compounding Pharmacy"
                        error={!!errors.pharmacy_name}
                      />
                    </FormField>

                    <FormField
                      label="Admin Email"
                      required
                      error={errors.admin_email?.message}
                      helperText="This email will be used to create your pharmacy admin account"
                    >
                      <Input
                        type="email"
                        {...register('admin_email', {
                          required: 'Admin email is required',
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: 'Enter a valid email address',
                          },
                        })}
                        placeholder="admin@yourpharmacy.com"
                        error={!!errors.admin_email}
                      />
                    </FormField>

                    <FormField
                      label="Fax Number"
                      required
                      error={errors.fax_number?.message}
                      helperText="Prescriptions will be faxed to this number"
                    >
                      <Controller
                        name="fax_number"
                        control={control}
                        rules={{
                          required: 'Fax number is required',
                          validate: {
                            validLength: (value: string) => {
                              const digits = stripPhoneFormatting(value)
                              return digits.length === 10 || 'Fax number must be 10 digits'
                            },
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            type="tel"
                            placeholder="(555) 123-4567"
                            error={!!errors.fax_number}
                            value={formatPhoneNumber(field.value || '')}
                            onChange={(e) =>
                              field.onChange(formatPhoneNumber(e.target.value))
                            }
                            onBlur={field.onBlur}
                          />
                        )}
                      />
                    </FormField>
                  </div>
                </Card>

                {/* Section 2: Branding */}
                <Card header="Branding" className="max-w-2xl mx-auto">
                  <div className="space-y-5">
                    <FileUploadField
                      token={token!}
                      fileType="logo"
                      label="Pharmacy Logo"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      maxSizeMB={5}
                      value={logoFileUrl}
                      fileName={logoFileName}
                      onChange={(url, name) => {
                        setValue('logo_file_url', url)
                        setValue('logo_file_name', name)
                      }}
                      helperText="PNG, JPG, GIF, or WebP. Used in your portal header."
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ColorPickerField
                        label="Primary Color"
                        value={primaryColor}
                        onChange={(c) => setValue('primary_color', c)}
                        defaultColor="#5C288F"
                        helperText="Buttons, links, accents"
                      />
                      <ColorPickerField
                        label="Secondary Color"
                        value={secondaryColor}
                        onChange={(c) => setValue('secondary_color', c)}
                        defaultColor="#BBA2FE"
                        helperText="Focus rings, backgrounds"
                      />
                    </div>
                  </div>
                </Card>

                {/* Section 3: Portal URL */}
                <Card header="Portal URL" className="max-w-2xl mx-auto">
                  <SlugInput
                    token={token!}
                    value={preferredSlug}
                    onChange={(slug) => setValue('preferred_slug', slug, { shouldValidate: true })}
                    error={errors.preferred_slug?.message}
                  />
                  {/* Hidden validation for react-hook-form */}
                  <input
                    type="hidden"
                    {...register('preferred_slug', {
                      required: 'Portal URL slug is required',
                      minLength: { value: 2, message: 'Slug must be at least 2 characters' },
                      pattern: {
                        value: /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
                        message: 'Only lowercase letters, numbers, and hyphens allowed',
                      },
                    })}
                  />
                </Card>

                {/* Section 4: Formulary */}
                <Card header="Formulary" className="max-w-2xl mx-auto">
                  <FileUploadField
                    token={token!}
                    fileType="formulary"
                    label="Formulary File"
                    accept=".pdf,.csv,.xls,.xlsx,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    maxSizeMB={20}
                    value={formularyFileUrl}
                    fileName={formularyFileName}
                    onChange={(url, name) => {
                      setValue('formulary_file_url', url)
                      setValue('formulary_file_name', name)
                    }}
                    helperText="Upload your product catalog/formulary (PDF, CSV, or Excel). We'll configure your products from this."
                  />
                </Card>

                {/* Section 5: Notes */}
                <Card header="Notes" className="max-w-2xl mx-auto">
                  <FormField
                    label="Additional Notes"
                    helperText="Anything else we should know about your pharmacy or portal preferences?"
                  >
                    <Textarea
                      {...register('notes')}
                      placeholder="e.g., We specialize in hormone therapy compounds..."
                      rows={4}
                    />
                  </FormField>
                </Card>

                {/* Section 6: Schedule Onboarding */}
                <Card header="Schedule Onboarding Call">
                  <p className="text-sm text-charcoal/70 mb-4">
                    Book a call with our team to walk through your portal setup and answer any questions.
                  </p>
                  <CalendarEmbed />
                </Card>

                {/* Submit */}
                <div className="max-w-2xl mx-auto pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    Submit Intake Form
                  </Button>
                  <p className="text-xs text-center text-charcoal/50 mt-3">
                    You can always update these details later with our team.
                  </p>
                </div>
              </form>
            </FormProvider>
          </div>
        )
    }
  }

  return <MarketingLayout nav={nav}>{renderContent()}</MarketingLayout>
}

export default IntakeForm
